import { FC, useMemo } from 'react'

import { NodeProps, Position } from '@xyflow/react'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  Text,
  GreyGrey70,
  Primary13Regular,
  Primary13SemiBold,
  Flex
} from '@sequencehq/core-components'
import {
  focusedSwitchGroupAtom,
  presentationAtom,
  selectedNodeFieldAtom
} from 'Automations/automationEditor/atoms'
import {
  EditorNodeTemplate,
  NodeHandle
} from 'Automations/nodeDefinitions/components'
import { ConditionalProperties } from 'Automations/nodeDefinitions/conditions'
import { useNode } from 'Automations/nodeDefinitions/hooks'
import { useUpstreamConditionalProperties } from './hooks/useUpstreamConditionalProperties'
import { Config, SwitchGroup } from './index'
import { formatConditionText, ConditionTextParts } from './helpers'
import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import { IF_LABEL, ELSE_HANDLE_ID, ELSE_HANDLE_LABEL } from './switch.constants'

export const SwitchEditorNode: FC<NodeProps> = ({ id, selected }) => {
  const { nodeData } = useNode<Config>({ nodeId: id })
  const presentation = useAtomValue(presentationAtom)
  const setSelectedNodeField = useSetAtom(selectedNodeFieldAtom)
  const focusedSwitchGroup = useAtomValue(focusedSwitchGroupAtom)

  const conditionalProperties = useUpstreamConditionalProperties(
    id,
    presentation ?? undefined
  )

  if (!nodeData) return null

  return (
    <EditorNodeTemplate nodeId={id} width="315px" selected={selected}>
      <EditorNodeTemplate.Rows>
        {nodeData.groups.map(group => (
          <EditorNodeTemplate.Row
            key={group.id}
            data-testid={`automations.editor.node.switch.group.${group.id}`}
            onClick={
              conditionalProperties
                ? () =>
                    setSelectedNodeField({
                      nodeId: id,
                      fieldId: `groups.${group.id}`
                    })
                : undefined
            }
          >
            <GroupConditionText
              group={group}
              conditionalProperties={conditionalProperties}
              nodes={presentation?.nodes ?? []}
            />
            <NodeHandle
              nodeId={id}
              handleMapping={group.id}
              type="source"
              position={Position.Right}
              selected={selected && focusedSwitchGroup === group.id}
              insideRow
            />
          </EditorNodeTemplate.Row>
        ))}

        <EditorNodeTemplate.Row
          data-testid="automations.editor.node.switch.else"
          onClick={() =>
            setSelectedNodeField({
              nodeId: id,
              fieldId: `groups.${ELSE_HANDLE_ID}`
            })
          }
        >
          <EditorNodeTemplate.Row.Text>
            {ELSE_HANDLE_LABEL}
          </EditorNodeTemplate.Row.Text>
          <NodeHandle
            nodeId={id}
            handleMapping={ELSE_HANDLE_ID}
            type="source"
            position={Position.Right}
            selected={selected && focusedSwitchGroup === ELSE_HANDLE_ID}
            insideRow
          />
        </EditorNodeTemplate.Row>
      </EditorNodeTemplate.Rows>

      <NodeHandle
        nodeId={id}
        type="target"
        position={Position.Left}
        selected={selected}
      />
    </EditorNodeTemplate>
  )
}

const GroupConditionText: FC<{
  group: SwitchGroup
  conditionalProperties: ConditionalProperties | undefined
  /** For a property naming another node — the review step of an approval. */
  nodes: AutomationsPresentationV1Node[]
}> = ({ group, conditionalProperties, nodes }) => {
  const formattedConditions = useMemo(() => {
    if (!conditionalProperties) return []

    return group.conditions.reduce<ConditionTextParts[]>((acc, condition) => {
      const parts = formatConditionText(condition, conditionalProperties, nodes)
      if (parts) acc.push(parts)
      return acc
    }, [])
  }, [group.conditions, conditionalProperties, nodes])

  const hasLabel = Boolean(group.label && group.label.trim().length > 0)

  if (formattedConditions.length === 0 && !hasLabel) {
    return <EditorNodeTemplate.Row.Text>{IF_LABEL}</EditorNodeTemplate.Row.Text>
  }

  if (hasLabel) {
    return (
      <EditorNodeTemplate.Row.Text
        data-testid={`automations.editor.node.switch.group.${group.id}.label`}
        strong
      >
        {group.label}
      </EditorNodeTemplate.Row.Text>
    )
  }

  return (
    <Flex flexDir="column" gap="8px">
      {formattedConditions.map((parts, index) => (
        <Text
          key={index}
          data-testid={`automations.editor.node.switch.group.${group.id}.condition.${group.conditions[index]?.id}.text`}
          {...Primary13Regular}
          color={GreyGrey70}
          textAlign="right"
          // `overflowWrap="anywhere"` keeps natural word boundaries the
          // preferred break point but still allows breaking inside very long
          // tokens to prevent the row overflowing the node — `break-all`
          // breaks mid-word too eagerly, splitting normal labels like
          // "Total contract value" awkwardly.
          overflowWrap="anywhere"
        >
          <Text as="span" color={GreyGrey70}>
            {parts.propertyLabel}
          </Text>{' '}
          <Text as="span" {...Primary13SemiBold} color={GreyGrey70}>
            {parts.operatorLabel}
          </Text>{' '}
          {parts.valueLabel}
        </Text>
      ))}
    </Flex>
  )
}
